import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiLoader, FiPause, FiPlay, FiRadio, FiSearch, FiVolume2 } from 'react-icons/fi';
import { Popover, useToast } from '../ui';
import { DEFAULT_STATION, RADIO_GROUPS, RADIO_STATIONS } from './radioStations';
import { t } from '../i18n';

const KEY = 'gq-radio';
const load = () => {
  try { const s = JSON.parse(localStorage.getItem(KEY)) || {}; return { id: RADIO_STATIONS.some((x) => x.id === s.id) ? s.id : DEFAULT_STATION, volume: typeof s.volume === 'number' ? Math.min(1, Math.max(0, s.volume)) : 0.7 }; } catch (e) { return { id: DEFAULT_STATION, volume: 0.7 }; }
};

/** Radio in the top bar: the button plays / pauses; the arrow opens the list of stations (Israel, Miami, USA, Colombia, Europe). */
export default function Radio() {
  const toast = useToast();
  const audio = useRef(null);
  const [pref, setPref] = useState(load);
  const [state, setState] = useState('idle'); // idle | loading | playing
  const [q, setQ] = useState('');
  const station = RADIO_STATIONS.find((s) => s.id === pref.id) || RADIO_STATIONS[0];

  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(pref)); } catch (e) { /* ignore */ } }, [pref]);
  useEffect(() => { if (audio.current) audio.current.volume = pref.volume; }, [pref.volume]);

  const play = (s = station) => {
    const a = audio.current;
    if (!a) return;
    setState('loading');
    if (!a.src || a.dataset.id !== s.id) { a.src = s.url; a.dataset.id = s.id; }
    a.play().catch((e) => {
      setState('idle');
      if (e && e.name !== 'AbortError') toast(t("This station cannot be played right now. Try another one."), 'warning');
    });
  };
  const stop = () => {
    const a = audio.current;
    if (!a) return;
    a.pause();
    a.removeAttribute('src'); delete a.dataset.id; a.load(); // closes the connection: a live stream must not keep downloading in silence
    setState('idle');
  };
  const toggle = () => (state === 'idle' ? play() : stop());
  const choose = (s) => { setPref((p) => ({ ...p, id: s.id })); play(s); };
  useEffect(() => () => { if (audio.current) audio.current.pause(); }, []);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return RADIO_GROUPS.map((g) => ({ ...g, stations: g.stations.filter((s) => !needle || `${s.name} ${s.country || ''} ${g.group}`.toLowerCase().includes(needle)) })).filter((g) => g.stations.length);
  }, [q]);

  return (
    <>
      <span className="radio-wrap">
        <button className="icon-btn radio-btn" onClick={toggle} title={state === 'idle' ? `${t("Play radio")}: ${station.name}` : `${t("Stop radio")}: ${station.name}`} aria-pressed={state !== 'idle'} style={state !== 'idle' ? { color: 'var(--primary)' } : undefined}>
          {state === 'loading' ? <FiLoader className="spin" /> : <FiRadio />}
        </button>
        <Popover align="right" trigger={<button className="icon-btn radio-more" aria-label={t("Choose a radio station")} title={t("Choose a radio station")}><FiChevronDown /></button>}>
          <div className="radio-panel" onKeyDown={(e) => e.stopPropagation()}>
            <div className="radio-now">
              <button className="radio-play" onClick={toggle} aria-label={state === 'idle' ? t("Play radio") : t("Stop radio")}>{state === 'idle' ? <FiPlay /> : <FiPause />}</button>
              <div className="radio-now-text">
                <b dir="auto">{station.name}</b>
                <span>{state === 'playing' ? t("Playing") : state === 'loading' ? t("Connecting…") : t("Stopped")}</span>
              </div>
              <FiVolume2 aria-hidden="true" />
              <input className="radio-vol" type="range" min="0" max="1" step="0.05" value={pref.volume} onChange={(e) => setPref((p) => ({ ...p, volume: Number(e.target.value) }))} aria-label={t("Volume")} />
            </div>
            <div className="radio-search"><FiSearch /><input className="input" placeholder={t("Search stations")} aria-label={t("Search stations")} value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div className="radio-list">
              {groups.map((g) => (
                <div key={g.group}>
                  <div className="radio-group">{t(g.group)}</div>
                  {g.stations.map((s) => (
                    <button key={s.id} className={`radio-item ${s.id === station.id ? 'on' : ''}`} onClick={() => choose(s)}>
                      <span dir="auto" className="radio-name">{s.name}</span>
                      {s.country && <span className="radio-country">{t(s.country)}</span>}
                      {s.id === station.id && state === 'playing' && <span className="radio-eq" aria-hidden="true"><i /><i /><i /></span>}
                    </button>
                  ))}
                </div>
              ))}
              {groups.length === 0 && <div className="radio-empty">{t("No station matches.")}</div>}
            </div>
            <div className="radio-note">{t("Some stations only play in their own country.")}</div>
          </div>
        </Popover>
      </span>
      <audio
        ref={audio}
        preload="none"
        onPlaying={() => setState('playing')}
        onWaiting={() => setState((s) => (s === 'idle' ? s : 'loading'))}
        onError={() => { if (audio.current && audio.current.getAttribute('src')) { setState('idle'); toast(t("This station cannot be played right now. Try another one."), 'warning'); } }}
      />
    </>
  );
}
